/**
 * Story Analytics Dashboard
 * View and manage analytics for exported TweePorter stories
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  BarChart3, Users, MousePointerClick, Trophy, Clock, 
  Download, Trash2, RefreshCw, ChevronRight, ArrowLeft,
  Eye, Share2, QrCode
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import PageHeader from './PageHeader';
import { API_BASE_URL } from '../config/api';
import SEO from './SEO';

const StoryAnalytics = () => {
  const { storyId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionEvents, setSessionEvents] = useState([]);
  const [showQR, setShowQR] = useState(false);

  // Fetch analytics summary
  const fetchSummary = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/analytics/summary/${storyId}`);
      if (!response.ok) throw new Error('Failed to fetch analytics');
      const data = await response.json();
      setSummary(data);
    } catch (err) {
      setError(err.message);
    }
  }, [storyId]);

  // Fetch sessions list
  const fetchSessions = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/analytics/sessions/${storyId}?limit=50`);
      if (!response.ok) throw new Error('Failed to fetch sessions');
      const data = await response.json();
      setSessions(data.sessions || []);
    } catch (err) {
      console.error('Error fetching sessions:', err);
    }
  }, [storyId]);

  // Fetch session detail
  const fetchSessionDetail = async (sessionId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/analytics/session/${storyId}/${sessionId}`);
      if (!response.ok) throw new Error('Failed to fetch session');
      const data = await response.json();
      setSessionEvents(data.events || []);
      setSelectedSession(sessionId);
    } catch (err) {
      console.error('Error fetching session detail:', err);
    }
  };

  // Export analytics
  const handleExport = async (format) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/analytics/export/${storyId}?format=${format}`);
      if (!response.ok) throw new Error('Export failed');
      const data = await response.json();
      
      // Create download
      const blob = new Blob(
        [format === 'csv' ? data.data : JSON.stringify(data.events, null, 2)],
        { type: format === 'csv' ? 'text/csv' : 'application/json' }
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics-${storyId}-${new Date().toISOString().split('T')[0]}.${format}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Export failed: ' + err.message);
    }
  };

  // Clear analytics
  const handleClear = async () => {
    if (!window.confirm('Are you sure you want to delete ALL analytics data for this story? This cannot be undone.')) {
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/analytics/clear/${storyId}?confirm=true`, {
        method: 'DELETE'
      });
      if (!response.ok) throw new Error('Clear failed');
      
      // Refresh data
      await fetchSummary();
      await fetchSessions();
      setSelectedSession(null);
      setSessionEvents([]);
      alert('Analytics data cleared successfully');
    } catch (err) {
      alert('Clear failed: ' + err.message);
    }
  };

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchSummary();
      await fetchSessions();
      setLoading(false);
    };
    loadData();
  }, [fetchSummary, fetchSessions]);

  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  // Format relative time
  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return '';
    const diff = Date.now() - new Date(timestamp).getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">Error: {error}</p>
          <Button onClick={() => navigate('/')}>Go Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO 
        title={`Analytics: ${summary?.title || storyId} | TweePorter`}
        description="View analytics and engagement metrics for your interactive story"
        canonicalUrl={`/story-analytics/${storyId}`}
      />

      <PageHeader borderBottom={true} />

      {/* Story Info Bar */}
      <div className="bg-white border-b sticky top-0 z-40">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-slate-900">
              {summary?.title || 'Story Analytics'}
            </h2>
            <p className="text-xs text-slate-500">ID: {storyId}</p>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate('/compiler')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Compiler
          </Button>
        </div>
      </div>

      <main className="container mx-auto px-4 py-6">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Users className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{summary?.total_sessions || 0}</p>
                  <p className="text-xs text-slate-500">Total Sessions</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Eye className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{summary?.total_events || 0}</p>
                  <p className="text-xs text-slate-500">Total Events</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Trophy className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{summary?.total_completions || 0}</p>
                  <p className="text-xs text-slate-500">Completions</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <BarChart3 className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">
                    {summary?.avg_path_length?.toFixed(1) || '0'}
                  </p>
                  <p className="text-xs text-slate-500">Avg Path Length</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-3 mb-6">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => { fetchSummary(); fetchSessions(); }}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => handleExport('json')}
          >
            <Download className="h-4 w-4 mr-2" />
            Export JSON
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => handleExport('csv')}
          >
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setShowQR(!showQR)}
          >
            <QrCode className="h-4 w-4 mr-2" />
            Share Link
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            className="text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={handleClear}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Clear Data
          </Button>
        </div>

        {/* Share Link Panel */}
        {showQR && (
          <Card className="mb-6 bg-indigo-50 border-indigo-200">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <Share2 className="h-6 w-6 text-indigo-600 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="font-semibold text-indigo-900 mb-2">Analytics Dashboard URL</h3>
                  <p className="text-sm text-indigo-700 mb-3">
                    Bookmark this link to access your analytics anytime. Only share with trusted team members.
                  </p>
                  <div className="bg-white rounded-lg p-3 border border-indigo-200">
                    <code className="text-sm text-slate-800 break-all">
                      {window.location.href}
                    </code>
                  </div>
                  <Button 
                    size="sm" 
                    className="mt-3"
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.href);
                      alert('Link copied to clipboard!');
                    }}
                  >
                    Copy Link
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          {/* Node Views */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Eye className="h-4 w-4" />
                Node Views
              </CardTitle>
            </CardHeader>
            <CardContent>
              {Object.keys(summary?.node_views || {}).length === 0 ? (
                <p className="text-slate-500 text-sm">No data yet</p>
              ) : (
                <div className="space-y-2">
                  {Object.entries(summary.node_views)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 10)
                    .map(([nodeId, count]) => {
                      const maxCount = Math.max(...Object.values(summary.node_views));
                      const percentage = (count / maxCount) * 100;
                      return (
                        <div key={nodeId} className="flex items-center gap-2">
                          <span className="text-xs text-slate-600 w-24 truncate" title={nodeId}>
                            {nodeId}
                          </span>
                          <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
                            <div 
                              className="bg-blue-500 h-full rounded-full transition-all"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                          <span className="text-xs text-slate-500 w-8 text-right">{count}</span>
                        </div>
                      );
                    })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Choice Distribution */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <MousePointerClick className="h-4 w-4" />
                Choice Distribution
              </CardTitle>
            </CardHeader>
            <CardContent>
              {Object.keys(summary?.choice_counts || {}).length === 0 ? (
                <p className="text-slate-500 text-sm">No data yet</p>
              ) : (
                <div className="space-y-2">
                  {Object.entries(summary.choice_counts)
                    .sort((a, b) => b[1] - a[1])
                    .slice(0, 10)
                    .map(([choiceId, count]) => {
                      const maxCount = Math.max(...Object.values(summary.choice_counts));
                      const percentage = (count / maxCount) * 100;
                      return (
                        <div key={choiceId} className="flex items-center gap-2">
                          <span className="text-xs text-slate-600 w-24 truncate" title={choiceId}>
                            {choiceId}
                          </span>
                          <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
                            <div 
                              className="bg-orange-500 h-full rounded-full transition-all"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                          <span className="text-xs text-slate-500 w-8 text-right">{count}</span>
                        </div>
                      );
                    })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sessions List */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Recent Sessions ({sessions.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {sessions.length === 0 ? (
              <p className="text-slate-500 text-sm text-center py-8">
                No sessions recorded yet. Analytics will appear here once visitors interact with your story.
              </p>
            ) : (
              <div className="divide-y">
                {sessions.map((session) => (
                  <div 
                    key={session.session_id}
                    className={`py-3 flex items-center justify-between cursor-pointer hover:bg-slate-50 -mx-6 px-6 transition-colors ${
                      selectedSession === session.session_id ? 'bg-indigo-50' : ''
                    }`}
                    onClick={() => fetchSessionDetail(session.session_id)}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-2 h-2 rounded-full ${session.completed ? 'bg-green-500' : 'bg-slate-300'}`} />
                      <div>
                        <p className="text-sm font-medium text-slate-800">
                          {session.session_id.substring(0, 8)}...
                        </p>
                        <p className="text-xs text-slate-500">
                          {formatRelativeTime(session.started_at)} • {session.event_count} events
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {session.completed && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                          Completed
                        </span>
                      )}
                      <ChevronRight className="h-4 w-4 text-slate-400" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Session Detail */}
        {selectedSession && sessionEvents.length > 0 && (
          <Card className="mt-6 border-indigo-200">
            <CardHeader>
              <CardTitle className="text-base">
                Session Detail: {selectedSession.substring(0, 8)}...
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {sessionEvents.map((event, index) => (
                  <div 
                    key={index}
                    className="flex items-start gap-3 text-sm p-2 bg-slate-50 rounded"
                  >
                    <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                      event.event_type === 'story_complete' ? 'bg-green-500' :
                      event.event_type === 'choice_select' ? 'bg-orange-500' :
                      'bg-blue-500'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-800">{event.event_type}</span>
                        <span className="text-xs text-slate-400">
                          {new Date(event.timestamp).toLocaleTimeString()}
                        </span>
                      </div>
                      <p className="text-xs text-slate-600">
                        Node: {event.node_id}
                        {event.choice_id && ` → Choice: ${event.choice_id}`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Time Range */}
        {summary?.first_event && (
          <div className="mt-6 text-center text-sm text-slate-500">
            Data from {formatTime(summary.first_event)} to {formatTime(summary.last_event)}
          </div>
        )}
      </main>
    </div>
  );
};

export default StoryAnalytics;
